import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, uploadWithProgress } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Button, Field } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PEOPLE_QUERY_KEY, PERSON_QUERY_KEY } from './types';

export function SignatureField({ userId, name, hasSignature }: { userId: string; name: string; hasSignature: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [present, setPresent] = useState(hasSignature);
  const [progress, setProgress] = useState<number | null>(null);

  const preview = useQuery({
    queryKey: ['signature-preview', userId],
    queryFn: () => get<{ dataUri: string | null }>(`/org/users/${userId}/signature/preview`),
    enabled: present,
    staleTime: Infinity,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['signature-preview', userId] });
    void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PERSON_QUERY_KEY });
  };

  const upload = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    setProgress(0);
    try {
      await uploadWithProgress(`/org/users/${userId}/signature`, body, setProgress);
      setPresent(true);
      toast.success(tr('admin', 'signature_saved'), tr('admin', 'it_prints_on_prescriptions_and_reports_this'));
      refresh();
    } catch (caught) {
      toast.error(tr('admin', 'could_not_save_the_signature'), describeError(caught));
    } finally {
      setProgress(null);
    }
  };

  const remove = useMutation({
    mutationFn: () => del(`/org/users/${userId}/signature`),
    onSuccess: () => {
      setPresent(false);
      toast.success(tr('admin', 'signature_removed'));
      refresh();
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_remove_the_signature'), describeError(caught)),
  });

  return (
    <Field label={tr('admin', 'signature')} hint={tr('admin', 'png_jpeg_or_webp_up_to_256_kb_printed_on', { name: name })}>
      <div className="flex flex-wrap items-center gap-3">
        {present && preview.data?.dataUri && (
          <img src={preview.data.dataUri} alt={`Signature of ${name}`} className="h-12 max-w-[12rem] rounded border bg-white object-contain p-1" />
        )}
        {!present && <span className="text-xs text-muted-foreground">{tr('admin', 'no_signature_yet')}</span>}
        <label className="cursor-pointer text-sm text-primary hover:underline focus-ring">
          {progress !== null ? tr('admin', 'uploading_x_2', { value: Math.round(progress * 100) }) : present ? tr('admin', 'replace') : tr('admin', 'upload')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            disabled={progress !== null}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void upload(file);
            }}
          />
        </label>
        {present && (
          <Button type="button" variant="ghost" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
            {tr('admin', 'remove')}
          </Button>
        )}
      </div>
    </Field>
  );
}
